import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '../../components/Form/Input';
import Select from 'react-select';
import '../../assets/styles/account/account.scss';
import '../../assets/styles/portfolio/portfolio.scss';
import CertificatesSection from "./CertificatesSection";
import FAQSection from "./FAQSection";
import {ModalOfficialPreview} from "./ModalOfficialPreview";

const ID_TYPES = [
    { value: 'edrpou', label: 'Код ЄДРПОУ' },
    { value: 'rnokpp', label: 'РНОКПП' },
    { value: 'passport', label: 'Серія та номер паспорта' },
];

const goalShape = yup.object({
    competency: yup.string().max(2000).required("Обов'язкове поле"),
    expected: yup.string().max(2000).required("Обов'язкове поле"),
});

const activityShape = yup.object({
    idx: yup.string().nullable(),
    activityType: yup.string().max(500).nullable(),
    certificateNumber: yup.string().nullable().optional(),
    competencies: yup.string().max(2000).nullable(),
    place: yup.string().max(300).nullable(),
    dateDuration: yup.string().max(200).nullable(),
    points: yup.string().max(50).nullable(),
});

const schema = yup.object({
    fio: yup.string().required('Вкажіть ПІБ'),
    workplace: yup.string().nullable(),
    position: yup.string().nullable(),
    id: yup.object({ type: yup.string().oneOf(ID_TYPES.map(x => x.value)).nullable(), value: yup.string().nullable() }),
    license: yup.object({
        licenseDate: yup.string().nullable(),
        series: yup.string().nullable(),
        number: yup.string().nullable(),
        orderDate: yup.string().nullable(),
        orderNumber: yup.string().nullable(),
    }),
    specialties: yup.array().of(yup.mixed()).default([]),
    contacts: yup.object({ phone: yup.string().nullable(), postalAddress: yup.string().nullable(), email: yup.string().email('Некоректний email').nullable() }),
    goals: yup.array().of(goalShape).min(1, 'Додайте щонайменше одну ціль'),
    recommendations: yup.string().nullable(),
    activities: yup.array().of(activityShape).default([]),
    attachments: yup.array().of(yup.string()).default(['', '', '']),
    year: yup.number().nullable(),
});

const emptyGoal = () => ({ competency: '', expected: '' });
const emptyActivity = () => ({ idx: '', certificateNumber: '', activityType: '', competencies: '', place: '', dateDuration: '', points: '' });

export default function PortfolioForm({ api = {}, t = (k) => k, onSaved }) {
    const cfg = useMemo(() => ({
        specialtiesUrl: '/api/v1/spa/dictionaries/medicalSpecialties',
        stateUrl: '/api/portfolio/state',
        saveUrl: '/api/portfolio/state',
        certificatesUrl: '/api/portfolio/certificates',
        aiUrl: '/api/portfolio/ai',
        pageSize: 20,
        ...api,
    }), [api]);

    const pdfRef = useRef(null);
    const currentYear = new Date().getFullYear();
    const years = useMemo(() => { const ys = []; for (let y = currentYear; y >= 2021; y--) ys.push(y); return ys; }, [currentYear]);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [optionsSpecialties, setOptionsSpecialties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [error, setError] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const previewScrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef({ x: 0, y: 0, sl: 0, st: 0 });
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);

    // --- ДОДАНО: стан для сертифікатів ---
    const [certSearch, setCertSearch] = useState({ fio: '', year: currentYear });
    const [certPage, setCertPage] = useState(0);
    const [certLoading, setCertLoading] = useState(false);
    const [certError, setCertError] = useState(null);
    const [certData, setCertData] = useState({ content: [], number: 0, totalPages: 0, totalElements: 0, first: true, last: true });
    const [certChecked, setCertChecked] = useState({}); // id: boolean

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(max-width: 1024px)');
        const handler = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', handler); else mq.addListener(handler);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', handler); else mq.removeListener(handler); };
    }, []);

    useEffect(() => {
        if (!showPreview || isMobile) return;
        // wait a tick for layout
        const id = requestAnimationFrame(() => {
            const el = document.querySelector('#official-preview-sticky');
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return () => cancelAnimationFrame(id);
    }, [showPreview, isMobile]);

    const { control, register, handleSubmit, setValue, getValues, watch, reset, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        mode: 'onBlur',
        shouldUnregister: true,
        defaultValues: {
            fio: '',
            position: '',
            workplace: '',
            id: { type: '', value: '' },
            license: { licenseDate: '', series: '', number: '', orderDate: '', orderNumber: '' },
            specialties: [],
            contacts: { phone: '', postalAddress: '', email: '' },
            goals: [],
            recommendations: '',
            activities: [],
            attachments: ['', '', ''],
            year: currentYear,
        }
    });
    const fioValue = watch('fio');
    const yearValue = watch('year');

    const { fields: goalFields, append: appendGoal, remove: removeGoal } = useFieldArray({ control, name: 'goals' });
    const { fields: activityFields, append: appendActivity, remove: removeActivity } = useFieldArray({ control, name: 'activities' });
    const activities = watch('activities');

    // Fetch specialties + current state
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                if (cfg.specialtiesUrl) {
                    const r = await fetch(cfg.specialtiesUrl);
                    const list = r.ok ? await r.json() : [];
                    const opts = (Array.isArray(list) ? list : []).map((x) => ({ value: String(x.id ?? x.code ?? x.value ?? x.name), label: x.name ?? x.label ?? String(x.id ?? x.code ?? x.value) }));
                    if (!cancelled) setOptionsSpecialties(opts);
                }
                if (cfg.stateUrl) {
                    const url = cfg.stateUrl + (cfg.stateUrl.includes('?') ? `&year=${selectedYear}` : `?year=${selectedYear}`);
                    const r2 = await fetch(url);
                    if (r2.ok) {
                        const data = await r2.json();
                        if (!cancelled && data && typeof data === 'object') {
                            const incoming = normalizeIncoming(data);
                            incoming.year = selectedYear;
                            setValue('year', selectedYear, { shouldDirty: false });
                            reset(incoming);
                            fetchCertificates(0, incoming.fio);
                        }
                    }
                }
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [cfg.specialtiesUrl, cfg.stateUrl, reset, selectedYear]);

    function normalizeIncoming(d) {
        return {
            fio: d.fio || '',
            workplace: d.workplace || '',
            position: d.position || '',
            id: { type: d.id?.type || '', value: d.id?.value || '' },
            license: {
                licenseDate: d.activity?.license?.licenseDate || '',
                series: d.activity?.license?.series || '',
                number: d.activity?.license?.number || '',
                orderDate: d.activity?.license?.orderDate || '',
                orderNumber: d.activity?.license?.orderNumber || '',
            },
            specialties: (() => { const raw = d.activity?.specialties ?? []; if (!Array.isArray(raw)) return []; return raw.map(s => typeof s === 'object' ? String(s.id ?? s.code ?? s.value ?? s) : String(s)); })(),
            contacts: { phone: d.contacts?.phone || '', postalAddress: d.contacts?.postalAddress || '', email: d.contacts?.email || '' },
            goals: Array.isArray(d.goals) && d.goals.length ? d.goals : [],
            recommendations: d.recommendations || '',
            activities: Array.isArray(d.activities) && d.activities.length
                ? d.activities.map(a => ({ certificateNumber: '', ...a }))
                : [],
            attachments: Array.isArray(d.attachments) && d.attachments.length ? d.attachments : ['', '', ''],
            year: d.year || currentYear,
        };
    }

    const onSubmit = async (values) => {
        setValue('year', selectedYear, { shouldDirty: false });
        const payload = buildPayload(values, selectedYear);
        if (cfg.saveUrl) {
            try {
                const r = await fetch(cfg.saveUrl, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                if (!r.ok) throw new Error(`Помилка збереження: ${r.status}`);
                onSaved && onSaved();
            } catch (e) { setError(e.message); }
        }
    };

    function buildPayload(v, year) {
        return {
            header: { appendixTitle: 'Додаток 4', appendixTrail: 'до Порядку проведення атестації працівників сфери охорони здоров’я (підпункт 2 пункту 1 розділу ІІІ)' },
            fio: v.fio,
            workplace: v.workplace,
            position: v.position,
            id: v.id,
            activity: { license: v.license, specialties: v.specialties },
            contacts: v.contacts,
            goals: v.goals,
            recommendations: v.recommendations,
            activities: v.activities,
            attachments: v.attachments,
            year: year,
            sign: { employeeFio: v.fio, headTitle: '(Керівник суб’єкта господарювання / ФОП)' },
        };
    }

    async function ensureLib(url, globalCheck) {
        if (globalCheck()) return;
        await new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = url; s.async = true; s.onload = resolve; s.onerror = () => reject(new Error('Не вдалося завантажити скрипт'));
            document.head.appendChild(s);
        });
    }

    async function exportPDF() {
        try {
            const snapshot = getValues();
            setPdfData(snapshot);
            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

            await ensureLib('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', () => window.html2canvas);
            await ensureLib('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', () => window.jspdf || window.jspdf?.jsPDF || (window.jspdf_umd && window.jspdf_umd.jsPDF));
            const { jsPDF } = window.jspdf || window.jspdf_umd || {};

            const node = pdfRef.current;
            const canvas = await window.html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

            let y = 0;
            let remaining = imgHeight;
            while (remaining > 0) {
                pdf.addImage(imgData, 'PNG', 0, -y, pageWidth, imgHeight);
                remaining -= pageHeight;
                y += pageHeight;
                if (remaining > 0) pdf.addPage();
            }

            const values = getValues();
            pdf.save(`portfolio_${(values?.year || selectedYear)}_${new Date().toISOString().slice(0,10)}.pdf`);
        } catch (e) {
            setError(e.message);
        } finally {
            setPdfData(null);
        }
    }

    // === Certificates ===
    async function fetchCertificates(nextPage = 0, fioArg) {
        if (!cfg.certificatesUrl) return;
        const fio = (fioArg !== undefined ? fioArg : fioValue)?.trim();
        if (!fio) { setCertError('Вкажіть ПІБ для пошуку'); return; }
        const year = Number(selectedYear);

        setCertLoading(true); setCertError(null);
        try {
            const url = new URL(cfg.certificatesUrl, window.location.origin);
            url.searchParams.set('name', fio);
            url.searchParams.set('year', String(year));
            url.searchParams.set('page', String(nextPage));
            url.searchParams.set('limit', String(cfg.pageSize || 20));
            const r = await fetch(url.toString());
            if (!r.ok) throw new Error(`Помилка запиту (${r.status})`);
            const data = await r.json();
            setCertData({
                content: Array.isArray(data?.content) ? data.content : [],
                number: Number(data?.number ?? nextPage),
                totalPages: Number(data?.totalPages ?? 0),
                totalElements: Number(data?.totalElements ?? 0),
                first: !!data?.first,
                last: !!data?.last,
            });
            setCertPage(Number(data?.number ?? nextPage));
        } catch (e) {
            setCertError(e.message);
        } finally {
            setCertLoading(false);
        }
    }

    function toggleCert(id, checked) {
        setCertChecked(prev => ({ ...prev, [id]: checked }));
    }

    function selectedCertificates() {
        return certData.content.filter(c => certChecked[c.id]);
    }

    function getNextActivityIdxBase() {
        const maxIdx = activities
            .map(a => parseInt(a?.idx, 10))
            .filter(n => !isNaN(n))
            .reduce((max, n) => Math.max(max, n), 0);
        return maxIdx; // база (0, якщо записів ще немає)
    }

    function addSelectedToActivities() {
        const picked = selectedCertificates();
        if (!picked.length) return;
        const base = getNextActivityIdxBase();

        const toAdd = picked.map((c, i) => ({
            idx: String(base + i + 1),
            certificateNumber: c?.id,
            activityType: c?.title ? `${c.kind}: ${c.title}` : 'Сертифікат',
            competencies: '',
            place: 'Україна',
            dateDuration: c?.date
                ? new Date(c.date).toLocaleDateString('uk-UA') + (c?.hours != null ? ` / ${c.hours} год.` : '')
                : (c?.year ? String(c.year) : ''),
            points: c?.points != null ? String(c.points) : (c?.hours != null ? String(c.hours) : ''),
        }));

        // додаємо батчем, щоб уникнути гонок під час кількох append поспіль
        appendActivity(toAdd, false);
    }

    async function generateGoalsFromAI() {
        const fio = (getValues('fio') || '').trim();
        const year = Number(getValues('year') || selectedYear);
        const activities = watch('activities') || [];
        setAiError(null);
        if (!fio) { setAiError('Вкажіть ПІБ у полі вище'); return; }
        setAiLoading(true);
        try {
            const r = await fetch('/api/portfolio/ai', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fio, year, activities }),
            });
            if (!r.ok) throw new Error(`AI-помилка: ${r.status}`);
            const data = await r.json();
            const state = data?.state || {};
            if (Array.isArray(state.goals)) {
                setValue('goals', state.goals.length ? state.goals : [{ competency: '', expected: '' }], { shouldDirty: true });
            }
            if (typeof state.recommendations === 'string') {
                setValue('recommendations', state.recommendations, { shouldDirty: true });
            }
            if (Array.isArray(state.activities)) {
                setValue('activities', state.activities.length ? state.activities : [{ idx: '', activityType: '', competencies: '', place: '', dateDuration: '', points: '' }], { shouldDirty: true });
            }
        } catch (e) {
            setAiError(e.message);
        } finally {
            setAiLoading(false);
        }
    }


    function addSelectedToAttachments() {
        const picked = selectedCertificates();
        if (!picked.length) return;
        const current = [...(getValues('attachments') || [])];
        picked.forEach(c => {
            if (c?.fileUrl) current.push(c.fileUrl);
            else if (c?.id) current.push(`${c.kind}:${c.id}`);
        });
        setValue('attachments', current, { shouldDirty: true });
    }

    const values = watch();
    const goalsValues = watch('goals') || [];
    const activitiesValues = watch('activities') || [];

    return (
        <section className="section flex-center">
            <div className="portfolio-layout" style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', width: '100%' }}>
                <div className="authentication-form registration-form" style={{ width: '100%', maxWidth: 980 }}>
                    <h1>Форма заповнення портфоліо</h1>

                    <div className="form-group select-block js-form-group" style={{maxWidth: 220}}>
                        <select className="form-select" value={selectedYear} onChange={(e) => { const y = Number(e.target.value); setSelectedYear(y); setValue('year', y, { shouldDirty: false }); }}>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <label style={{top: '-1.2rem'}} className="form-label select-label active">Рік портфоліо</label>
                    </div>


                    <div className="submit-block" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start', margin: '1rem' }}>
                        <button type="submit"  style={{width: '48%'}} form="portfolioForm" className="btn">Зберегти</button>
                        <button type="button" className="btn" style={{width: '48%'}} onClick={exportPDF}>Експорт у PDF</button>
                    </div>

                    {loading && <div className="info-text">Завантаження…</div>}
                    {error && <div className="error_text">{error}</div>}

                    <form id="portfolioForm" onSubmit={handleSubmit(onSubmit)}>
                        {/* === 1. Загальні відомості === */}
                        <Group id="grp-general" title="1. Загальні відомості" defaultOpen>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="fio" id="fio" register={register} inputClassName="form-input" labelClassName="form-label" label="ПІБ" autoComplete="off" onBlur={() => fetchCertificates(0)} />
                                {errors?.fio && <span className="error_text">{errors.fio.message}</span>}
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="workplace" id="workplace" register={register} inputClassName="form-input" labelClassName="form-label" label="Місце роботи" autoComplete="off" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="position" id="position" register={register} inputClassName="form-input" labelClassName="form-label" label="Займана посада" autoComplete="off" />
                            </div>
                        </Group>

                        {/* === 2. Юридичні реквізити === */}
                        <Group id="grp-legal" title="2. Юридичні реквізити (ідентифікатор, вид діяльності, ліцензії)">
                            <div className="form-group block-static-height select-block js-form-group">
                                <select name="id.type" ref={register} className="form-select" defaultValue="">
                                    <option value=""></option>
                                    {ID_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                                </select>
                                <label htmlFor="id.type" className="form-label select-label active">Тип реквізиту</label>
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="id.value" id="idValue" register={register} inputClassName="form-input" labelClassName="form-label" label="Значення реквізиту" autoComplete="off" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="date" name="license.licenseDate" id="licenseDate" register={register} inputClassName="form-input" labelClassName="form-label active" label="Ліцензія на провадження господарської діяльності від (дата)" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="license.series" id="licenseSeries" register={register} inputClassName="form-input" labelClassName="form-label" label="Серія ліцензії" autoComplete="off" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="license.number" id="licenseNumber" register={register} inputClassName="form-input" labelClassName="form-label" label="№ ліцензії" autoComplete="off" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="date" name="license.orderDate" id="orderDate" register={register} inputClassName="form-input" labelClassName="form-label active" label="Рішення про видачу ліцензії, затверджене наказом МОЗ від" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="license.orderNumber" id="orderNumber" register={register} inputClassName="form-input" labelClassName="form-label" label="№ наказу" autoComplete="off" />
                            </div>
                            <div className="form-group select-block js-form-group">
                                <label className="form-label active">Спеціальності</label>
                                <Controller
                                    name="specialties"
                                    control={control}
                                    defaultValue={[]}
                                    render={({ value, onChange }) => {
                                        const ids = (value || []).map(String);
                                        const selected = optionsSpecialties.filter(o => ids.includes(String(o.value)));
                                        const handleChange = (vals) => onChange((vals || []).map(v => String(v.value)));
                                        return (
                                            <>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                    <div style={{ fontSize: 12, opacity: 0.85 }}>Обрано: {selected.length} / {optionsSpecialties.length}</div>
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button type="button" className="btn" onClick={() => handleChange(optionsSpecialties)} disabled={!optionsSpecialties.length}>Обрати всі</button>
                                                        <button type="button" className="btn" onClick={() => handleChange([])} disabled={!selected.length}>Очистити</button>
                                                    </div>
                                                </div>
                                                <Select
                                                    isMulti
                                                    options={optionsSpecialties}
                                                    value={selected}
                                                    onChange={handleChange}
                                                    placeholder="Почніть вводити назву…"
                                                    classNamePrefix="form-select"
                                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </>
                                        );
                                    }}
                                />
                            </div>
                        </Group>

                        {/* === 3. Контактні дані === */}
                        <Group id="grp-contacts" title="3. Контактні дані">
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="contacts.phone" id="phone" register={register} inputClassName="form-input" labelClassName="form-label" label="Телефон" autoComplete="off" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="contacts.postalAddress" id="postalAddress" register={register} inputClassName="form-input" labelClassName="form-label" label="Поштова адреса" autoComplete="off" />
                            </div>
                            <div className="form-group block-static-height js-form-group">
                                <Input watch={watch} type="text" name="contacts.email" id="email" register={register} inputClassName="form-input" labelClassName="form-label" label="Email" autoComplete="off" />
                                {errors?.contacts?.email && <span className="error_text">{errors.contacts.email.message}</span>}
                            </div>
                        </Group>

                        {/* === 5.1 Сертифікати з API === */}
                        <CertificatesSection
                            certData={certData}
                            certPage={certPage}
                            certLoading={certLoading}
                            certError={certError}
                            certSearch={certSearch}
                            setCertSearch={setCertSearch}
                            years={years}
                            certChecked={certChecked}
                            toggleCert={toggleCert}
                            fetchCertificates={fetchCertificates}
                            addSelectedToActivities={addSelectedToActivities}
                            watch={watch}
                            Input={Input}
                            Group={Group}
                        />
                        <div className="submit-block" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start', margin: '1rem' }}>
                            <button type="button" style={{width: '100%'}} className="btn" onClick={generateGoalsFromAI} disabled={aiLoading || !fioValue?.trim()}>
                                {aiLoading ? 'Генерація…' : 'Згенерувати цілі та діяльності з AI (beta)'}
                            </button>
                        </div>

                        {/* === 4. Цілі професрозвитку === */}
                        <Group id="grp-goals" title={`5. Цілі професійного розвитку (${goalFields.length})`}>
                            <div className="form-group js-form-group">
                                <label className="form-label active" htmlFor="recommendations">Рекомендації за місцем роботи</label>
                                <textarea style={{height: '1.5rem', resize: 'vertical', width: '100%'}} name="recommendations" ref={register} id="recommendations" className="form-input" rows={4} />
                            </div>
                            {goalFields.map((field, i) => (
                                <div key={field.id} className="form-group js-form-group">
                                    <Controller
                                        name={`goals[${i}].competency`}
                                        control={control}
                                        defaultValue={field?.competency ?? ''}
                                        render={({ value, onChange, onBlur, name, ref }) => (
                                            <Input watch={watch} inputClassName="form-input" labelClassName="form-label" id={`goal_comp_${i}`} name={name}
                                                   value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" label={`Ціль ${i+1}: Компетентність`} />
                                        )}
                                    />
                                    <Controller
                                        name={`goals[${i}].expected`}
                                        control={control}
                                        defaultValue={field?.expected ?? ''}
                                        render={({ value, onChange, onBlur, name, ref }) => (
                                            <Input watch={watch} inputClassName="form-input" labelClassName="form-label" id={`goal_exp_${i}`} name={name}
                                                   value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" label={`Ціль ${i+1}: Очікуваний результат`} />
                                        )}
                                    />
                                    <div className="hide-show-link">
                                        <button type="button" className="btn-link remove-row"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeGoal(i); }}>
                                            Видалити
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <div className="submit-block"><button style={{width: '100%'}} type="button" className="btn" onClick={() => appendGoal(emptyGoal(), false)}>+ Додати ціль</button></div>
                        </Group>

                        {/* === 5. Види діяльності === */}
                        <Group id="grp-activities" title={`6. Види діяльності (${activityFields.length})`}>
                            {activityFields.map((field, i) => (
                                <div key={field.id} className="form-group js-form-group">
                                    <div className="form-group js-form-group">
                                        <Controller
                                            name={`activities[${i}].idx`}
                                            control={control}
                                            defaultValue={field?.idx ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                <Input label={`№ з/п`} watch={watch} inputClassName="form-input" labelClassName="form-label" id={`act_idx_${i}`} name={name} type="text" value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" />
                                            )}
                                        />
                                        <Controller
                                            name={`activities[${i}].activityType`}
                                            control={control}
                                            defaultValue={field?.activityType ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                <Input label={`Вид діяльності`} watch={watch} inputClassName="form-input" labelClassName="form-label" id={`act_type_${i}`} name={name} type="text" value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" />
                                            )}
                                        />
                                        <Controller
                                            name={`activities[${i}].competencies`}
                                            control={control}
                                            defaultValue={field?.competencies ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                <Input label={`Здобуті/вдосконалені компетентності`} watch={watch} inputClassName="form-input" labelClassName="form-label" id={`act_comp_${i}`} name={name} type="text" value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" />
                                            )}
                                        />
                                        <Controller
                                            name={`activities[${i}].place`}
                                            control={control}
                                            defaultValue={field?.place ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                <Input label={`Місце (країна)`} watch={watch} inputClassName="form-input" labelClassName="form-label" id={`act_place_${i}`} name={name} type="text" value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" />
                                            )}
                                        />
                                        <Controller
                                            name={`activities[${i}].dateDuration`}
                                            control={control}
                                            defaultValue={field?.dateDuration ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                <Input label={`Дата / тривалість`} watch={watch} inputClassName="form-input" labelClassName="form-label" id={`act_date_${i}`} name={name} type="text" value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" />
                                            )}
                                        />
                                        <Controller
                                            name={`activities[${i}].points`}
                                            control={control}
                                            defaultValue={field?.points ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                <Input label={`К-ть балів`} watch={watch} inputClassName="form-input" labelClassName="form-label" id={`act_points_${i}`} name={name} type="text" value={value ?? ''} onChange={onChange} onBlur={onBlur} register={ref} autoComplete="off" />
                                            )}
                                        />
                                        <Controller
                                            name={`activities[${i}].certificateNumber`}
                                            control={control}
                                            defaultValue={field?.certificateNumber ?? ''}
                                            render={({ value, onChange, onBlur, name, ref }) => (
                                                ''
                                            )}
                                        />
                                    </div>
                                    <div className="hide-show-link">
                                        <button type="button" className="btn-link remove-row" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeActivity(i); }}>Видалити</button>
                                    </div>
                                </div>
                            ))}
                            <div className="submit-block"><button style={{width: '100%'}} type="button" className="btn" onClick={() => appendActivity(emptyActivity(), false)}>+ Додати вид діяльності</button></div>
                        </Group>



                        {/* === 6. Додатки === */}
                        <Group id="grp-attachments" title="7. Додатки (оригінали документів, що підтверджують види діяльності та обліковані бали)">
                            {(watch('attachments') || []).map((v, i) => (
                                <div key={`att_${i}`} className="form-group js-form-group">
                                    <Input watch={watch} type="text" name={`attachments[${i}]`} id={`att_${i}`} register={register} inputClassName="form-input" labelClassName="form-label" label={`Посилання/опис додатку ${i + 1}`} />
                                </div>
                            ))}
                        </Group>

                        <div className="submit-block">

                        </div>

                        <div className="submit-block" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start', margin: '1rem' }}>
                            <Input watch={watch} style={{width: '48%'}} type="submit" value="Зберегти" className="btn" />
                            <button type="button" className="btn" style={{width: '48%'}} onClick={exportPDF}>Експорт у PDF</button>
                        </div>
                    </form>
                    <div id="faq-section">
                        <FAQSection />
                    </div>

                    {/* Невидимий офіційний макет для PDF (не друга форма) */}
                    <div ref={pdfRef} style={{ position: 'fixed', left: -99999, top: 0, width: '210mm' }}>
                        <OfficialLayout values={pdfData || values} specialties={optionsSpecialties} />
                    </div>
                </div>

                <ModalOfficialPreview
                    showPreview={showPreview}
                setShowPreview={setShowPreview}
                getValues={getValues}
                optionsSpecialties={optionsSpecialties}
                OfficialLayout={OfficialLayout}
                />
            </div>
        </section>
    );
}

function Group({ id, title, defaultOpen = true, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <fieldset id={id} className="form-group js-form-group" style={{ border: '1px solid #e0e0e0', borderRadius: 6, marginBottom: 16 }}>
            <legend className="form-label" style={{ fontWeight: 700, padding: '0 8px 16px' }}>
                {title}
            </legend>
            <div style={{ display: open ? 'block' : 'none', padding: '8px 12px 12px' }}>{children}</div>
        </fieldset>
    );
}

function OfficialLayout({ values, specialties = [] }) {
    const v = values || {};
    const specMap = (Array.isArray(specialties) ? specialties : []).reduce((acc, s) => { acc[String(s.value)] = s.label; return acc; }, {});
    const idTypeFull = {
        edrpou: 'код згідно з ЄДРПОУ',
        rnokpp: 'реєстраційний номер облікової картки платника податків',
        passport: 'серія (за наявності) та номер паспорта',
    }[v?.id?.type];
    const idValueText = v?.id?.value ? `${idTypeFull ? idTypeFull + ': ' : ''}${v.id.value}` : '';
    const specialtiesText = (v.specialties || []).map((x) => specMap[String(x)] || String(x)).join(', ');
    const currentYear = new Date().getFullYear();

    const thtd = { border: '1px solid #000', verticalAlign: 'top', lineHeight: 1.25, breakInside: 'avoid', pageBreakInside: 'avoid', fontSize: 12 };
    const underline = (text, isBlock = false, hasTrailingComa = false) => (
        isBlock ? (
            <div style={{ borderBottom: '1px solid #000', minHeight: 18, paddingBottom: 2, marginTop: 2, marginBottom: 2 }}>{text || ''}{hasTrailingComa ? ',' : ''}</div>
        ) : (
            <span style={{ borderBottom: '1px solid #000', minHeight: 18, paddingBottom: 2 }}>{text || ''}{hasTrailingComa ? ',' : ''}</span>
        )
    );

    return (
        <div style={{ width: '210mm', minHeight: '297mm', padding: '15mm', background: '#fff', color: '#000', fontFamily: '"Times New Roman", Times, serif' }}>
            {/* Шапка */}
            <div style={{ textAlign: 'right', fontSize: 12, lineHeight: 1.35 }}>
                <div>Додаток 4</div>
                <div>до Порядку проведення атестації</div>
                <div>працівників сфери охорони</div>
                <div>здоров’я</div>
                <div>(підпункт 2 пункту 1 розділу ІІІ)</div>
            </div>

            <div style={{ textAlign: 'center', marginTop: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Особисте освітнє портфоліо з результатами проходження</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>безперервного професійного розвитку</div>
                <div style={{ marginTop: 6, fontSize: 12 }}>за {(v.year || currentYear)} рік</div>
            </div>

            {/* Загальні поля 1:1 з PDF-текстами */}
            <div style={{ marginTop: 10, fontSize: 12 }}>
                <div>Прізвище, ім’я, по батькові (за наявності)</div>
                {underline(v.fio, true)}

                <div style={{ marginTop: 6 }}>Місце роботи (за наявності) (повна назва суб’єкта господарювання / прізвище, ім’я,
                    по батькові (за наявності) фізичної особи – підприємця, код згідно з ЄДРПОУ (для юридичних
                    осіб) або реєстраційний номер облікової картки платника податків (для фізичних осіб) або
                    серію (за наявності) та номер паспорта (для фізичних осіб, які через свої релігійні
                    переконання відмовляються від прийняття реєстраційного номера облікової картки платника
                    податків та офіційно повідомили про це відповідному контролюючому органу і мають
                    відмітку у паспорті)</div>
                {underline(v.workplace + ', ' + idValueText, true, true)}

                <div style={{ marginTop: 6 }}>що провадить господарську діяльність з медичної практики / банків пуповинної крові, інших
                    тканин і клітин людини згідно з переліком, затвердженим Міністерством охорони здоров’я
                    України, або заготівлі та тестування донорської крові та компонентів крові незалежно від їх
                    кінцевого призначення, переробки, зберігання, розподілу та реалізації донорської крові та
                    компонентів крові, призначених для трансфузії, або діяльність з виробництва лікарських
                    засобів, оптової та роздрібної торгівлі лікарськими засобами на підставі
                    {v?.license?.number ? ` ліцензії від ${v.license.licenseDate || ''}, серія ${v.license.series || ''}, № ${v.license.number || ''}` : ''}
                    {v?.license?.orderNumber ? ` рішення про видачу ліцензії, затверджене наказом МОЗ від ${v.license.orderDate || ''}, № ${v.license.orderNumber || ''}` : ''}
                    {` за спеціальностями:`}
                    {underline(specialtiesText)}
                </div>

                <div style={{ marginTop: 6 }}>Займана посада: {underline(v.position || '')}</div>


                <div style={{ marginTop: 6 }}>Контактні дані (телефон, поштова адреса, адреса електронної пошти)</div>
                {underline(v?.contacts ? `${v.contacts.phone || ''}, ${v.contacts.postalAddress || ''}, ${v.contacts.email || ''}` : '')}
                {underline('')}
            </div>

            {/* Розділ I */}
            <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700 }}>I. Індивідуальні цілі професійного розвитку на рік</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} cellPadding={6}>
                <colgroup>
                    <col style={{ width: '50%' }} />
                    <col style={{ width: '50%' }} />
                </colgroup>
                <thead>
                <tr>
                    <th style={thtd}>Опис компетентності (знання, уміння / навички), що планується набути, розвинути або вдосконалити</th>
                    <th style={thtd}>Очікуваний результат (як планується застосовувати набутий професійний досвід у практичній діяльності, впровадження інноваційних методів роботи)</th>
                </tr>
                </thead>
                <tbody>
                {(((v.goals||[]).filter(g => (g && (String(g.competency||'').trim() || String(g.expected||'').trim())))).length ? (v.goals||[]).filter(g => (g && (String(g.competency||'').trim() || String(g.expected||'').trim()))) : [emptyGoal()]).map((g, i) => (
                    <tr key={i} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        <td style={thtd}>{g.competency || '\u00A0'}</td>
                        <td style={thtd}>{g.expected || '\u00A0'}</td>
                    </tr>
                ))}
                </tbody>
            </table>

            <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 12 }}>Рекомендації за місцем роботи щодо розвитку професійних компетентностей</div>
                <div style={{ border: '1px solid #000', minHeight: 60, padding: 6, whiteSpace: 'pre-wrap', fontSize: 12 }}>{v.recommendations || '\u00A0'}</div>
            </div>

            {/* Розділ II */}
            <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700 }}>II. Інформація про види діяльності та здобуті компетентності</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} cellPadding={6}>
                <colgroup>
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                <tr>
                    <th style={thtd}>№ з/п</th>
                    <th style={thtd}>Вид діяльності</th>
                    <th style={thtd}>Здобуті та/або вдосконалені компетентності</th>
                    <th style={thtd}>Місце проведення<sup>*</sup></th>
                    <th style={thtd}>Дата / тривалість</th>
                    <th style={thtd}>Кількість балів</th>
                </tr>
                </thead>
                <tbody>
                {(((v.activities||[]).filter(a => {const k=['idx','activityType','competencies','place','dateDuration','points']; return a && k.some(kk => String(a[kk]||'').trim());})).length ? (v.activities||[]).filter(a => {const k=['idx','activityType','competencies','place','dateDuration','points']; return a && k.some(kk => String(a[kk]||'').trim());}) : [emptyActivity()]).map((a, i) => (
                    <tr key={i} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        <td style={thtd}>{a.idx || i + 1}</td>
                        <td style={thtd}>{a.activityType || '\u00A0'}</td>
                        <td style={thtd}>{a.competencies || '\u00A0'}</td>
                        <td style={thtd}>{a.place || '\u00A0'}</td>
                        <td style={thtd}>{a.dateDuration || '\u00A0'}</td>
                        <td style={thtd}>{a.points || '\u00A0'}</td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Додатки */}
            <div style={{ marginTop: 10, fontSize: 12 }}>
                <div><b>Додатки</b> (оригінали документів, у т. ч. сертифікатів, що підтверджують проходження заходів БПР)</div>
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                    {(v.attachments || []).filter(Boolean).map((a, i) => (<li key={i}>{underline(a, true)}</li>))}
                    {!(v.attachments||[]).filter(Boolean).length ? '' : null}
                </ul>
            </div>

            {/* Підписи */}
            <div style={{ marginTop: 14, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ width: '48%' }}>
                        <div style={{ marginTop: 6 }}>{underline(v.fio || '', true)}</div>
                        <div style={{ textAlign: 'center' }}>(прізвище, ім’я, по батькові (за наявності))</div>
                    </div>
                    <div style={{ width: '48%' }}>
                        <div style={{ marginTop: 6 }}>{underline('', true)}</div>
                        <div style={{ textAlign: 'center' }}>(підпис)</div>
                    </div>
                </div>
                <div style={{marginTop: 10, display: 'flex', justifyContent: 'space-between'}}>
                    <div style={{ width: '48%' }}>
                        <div>Керівник суб’єкта господарювання /</div>
                        <div>Фізична особа – підприємець</div>
                    </div>
                    <div style={{ width: '48%', alignContent: 'end' }}>
                        <div style={{ textAlign: 'center', borderTop: "1px solid #000" }}>(прізвище, ім’я, по батькові (за наявності))</div>
                    </div>
                </div>
                <div style={{ marginTop: 10 }}>Кваліфікований електронний підпис керівника суб’єкта господарювання / фізичної особи – підприємця</div>
            </div>
        </div>
    );
}
